import Component from '@glimmer/component'
import { ThemeKeys } from 'teacher-ui-client/utils/themes'
import BasicTable from 'teacher-ui-client/components/stable-table/basic-table'

/**
 * Provide this component with labels and values to make a single row table
 *
 * tableData: [{ label: 'Gold', value: 0 }, { label: 'Silver', value: 1 }, { label: 'Bronze', value: 2 } ]
 *
 * Renders a table like this;
 *
 * Gold | Silver | Bronze
 *  0   |   1    |   2
 */

interface Args {
  theme?: ThemeKeys
  tableData: TableDataItem[]
}

interface Signature {
  Args: Args
  Element: HTMLTableElement
}

interface Theme {
  headerBg: string
  tableBorder: string
}

export interface TableDataItem {
  label: string
  value?: number
}

const themes: Partial<Record<ThemeKeys, Theme>> = {
  [ThemeKeys.blue]: { headerBg: 'bg-oceany-blue-50', tableBorder: 'border-oceany-blue-150' },
  [ThemeKeys.green]: { headerBg: 'bg-ms-green-50', tableBorder: 'border-ms-green-150' },
  [ThemeKeys.default]: { headerBg: 'bg-dusty-black-50', tableBorder: 'border-dusty-black-150' },
}

export class StudentDashboardSingleRowTable extends Component<Signature> {
  get selectedTheme() {
    return themes[this.args.theme || 'default']
  }

  get columns() {
    return this.args.tableData.map((column) => {
      return {
        value: column.label,
        classNames: `table-box-header text-center ${this.selectedTheme?.headerBg} border-l border-white`,
        cellClassNames: `table-box-cell text-center border-b ${this.selectedTheme?.tableBorder} print:border-black`,
      }
    })
  }

  get rows() {
    const cells = this.args.tableData.map((column) => {
      return { value: column.value }
    })

    return [{ cells }]
  }

  <template>
    <BasicTable
      class="w-full border-t-4 {{this.selectedTheme.tableBorder}} print:border-black"
      @columns={{this.columns}}
      @rows={{this.rows}}
      ...attributes
      as |bt|
    >
      <bt.head />
      <bt.body />
    </BasicTable>
  </template>
}

export default StudentDashboardSingleRowTable
